import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import { Autoplay, Thumbs, Mousewheel } from 'swiper/modules';
import styled, { keyframes } from 'styled-components';
import { ChevronLeft, ChevronRight, Search, X, ChevronDown, Trophy } from "lucide-react";
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useGameEngine, getGameImage } from 'hooks/useGameEngine';

const OverlayContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  flex-direction: column;
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
`;

const HeaderPanel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background-color: #1a2c38;
  padding: 18px 16px 16px 16px;
  z-index: 10;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: #0f212e;
  border-radius: 8px;
  flex: 1;
  height: 48px;
  padding: 0 8px;
  border: 2px solid transparent;
  transition: border-color 0.2s;
  &:focus-within {
    border-color: #557086;
  }
`;

const CategorySelector = styled.div`
  position: relative;
`;

const CategoryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 36px;
  background: transparent;
  border: none;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  outline: none;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 48px;
  left: 0;
  width: 160px;
  background-color: #1a2c38;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.5);
  border: 1px solid rgba(255,255,255,0.1);
  padding: 4px;
  z-index: 20;
`;

const DropdownItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border-radius: 6px;
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  &:hover {
    background-color: rgba(255,255,255,0.05);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  padding: 0 12px;
  font-size: 14px;
  &::placeholder {
    color: #94a6b5;
  }
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  background: transparent;
  border: none;
  color: #94a6b5;
  cursor: pointer;
  &:hover {
    color: #fff;
  }
`;

const ResultsContainer = styled.div`
  margin-top: 16px;
  max-height: 70vh;
  overflow-y: auto;
  
  /* Scrollbar styles */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #2f4553;
    border-radius: 4px;
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const LoaderWrapper = styled.div`
  display: grid;
  place-items: center;
  padding: 80px 0;
`;

const ProgressBarContainer = styled.div`
  width: 48px;
  height: 6px;
  background-color: #0f212e;
  border-radius: 99px;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  height: 100%;
  width: 33%;
  background-color: rgba(255,255,255,0.7);
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const GridItem = styled.div`
  background-color: #292d2e;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  aspect-ratio: 3 / 4;

  .game-img {
    width: 100%;
    height: 100%;
    object-fit: fill;
    transition: transform 0.3s ease;
  }
  
  &:hover .game-img {
    transform: scale(1.05);
  }

  .play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    opacity: 0;
    padding: 12px;
    background-color: rgba(0,0,0,0.6);
    border-radius: 50%;
    transition: opacity 0.2s ease;
    z-index: 2;
  }

  &:hover .play-icon {
    opacity: 1;
  }
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 20px 0;
`;

const ErrorTitle = styled.div`
  color: #ff5a7a;
  font-weight: bold;
  margin-top: 16px;
`;

const ErrorSub = styled.div`
  color: #94a6b5;
  font-size: 14px;
  margin-top: 4px;
`;

const SuggestionRowContainer = styled.div`
  margin-top: 24px;
  text-align: left;
`;

const SuggestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  
  h3 {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #fff;
    font-weight: bold;
    margin: 0;
    font-size: 16px;
  }
`;

const NavButtons = styled.div`
  display: flex;
  gap: 8px;
  
  button {
    width: 32px;
    height: 32px;
    display: grid;
    place-items: center;
    border-radius: 6px;
    background-color: #0f212e;
    color: #fff;
    border: none;
    cursor: pointer;
    &:hover { background-color: #2f4553; }
  }
`;

export default function ExploreModal({ onClose }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("casino");
  const [catOpen, setCatOpen] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [debouncedQ, setDebouncedQ] = useState("");
  const history = useHistory();

  // Fetch games based on debounced search query
  const { data: fetchedGames, isLoading } = useGameEngine(debouncedQ ? { search: debouncedQ, limit: 30 } : { limit: 30 });
  const games = fetchedGames || [];

  useEffect(() => {
    if (!q.trim()) {
      setDebouncedQ("");
      setPhase("idle");
      return;
    }
    setPhase("loading");
    const t = setTimeout(() => {
      setDebouncedQ(q.trim());
    }, 600);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (debouncedQ) {
      if (isLoading) {
        setPhase("loading");
      } else {
        setPhase(games.length ? "results" : "error");
      }
    }
  }, [debouncedQ, games, isLoading]);

  const matches = games;

  return (
    <OverlayContainer>
      <Backdrop onClick={onClose} />
      <HeaderPanel>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '100%' }}>
          <SearchInputWrapper>
            <CategorySelector>
              <CategoryButton onClick={() => setCatOpen((v) => !v)}>
                {cat === "casino" ? "Casino" : "Sports"}
                <ChevronDown size={16} style={{ transform: catOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
              </CategoryButton>
              {catOpen && (
                <DropdownMenu>
                  <DropdownItem onClick={() => { setCat("casino"); setCatOpen(false); }}>Casino</DropdownItem>
                  <DropdownItem onClick={() => { setCat("sports"); setCatOpen(false); }}>Sports</DropdownItem>
                </DropdownMenu>
              )}
            </CategorySelector>
            
            <Search size={16} color="#94a6b5" style={{ marginLeft: '8px' }} />
            
            <SearchInput 
              autoFocus 
              value={q} 
              onChange={(e) => setQ(e.target.value)} 
              placeholder="Search your game" 
            />
            
            <CloseButton onClick={onClose}>
              <X size={16} />
            </CloseButton>
          </SearchInputWrapper>
        </div>

        <ResultsContainer>
          {phase === "loading" && (
            <LoaderWrapper>
              <ProgressBarContainer>
                <ProgressBar />
              </ProgressBarContainer>
            </LoaderWrapper>
          )}

          {phase === "results" && (
            <GridContainer>
              {matches.slice(0, 30).map((g, i) => (
                <GridItem key={i} onClick={() => { onClose(); history.push(`/play/${g.id}`); }}>
                  <img src={getGameImage(g)} alt={g.name} className="game-img" />
                  <i className="fa fa-play play-icon"></i>
                </GridItem>
              ))}
            </GridContainer>
          )}

          {phase === "error" && (
            <ErrorContainer>
              <ErrorTitle>No games found</ErrorTitle>
              <ErrorSub>Try searching with a different keyword</ErrorSub>
              
              <SuggestionRowContainer>
                <SuggestionHeader>
                  <h3><Trophy size={20} /> Suggested Games</h3>
                  <NavButtons>
                    <button><ChevronLeft size={16} /></button>
                    <button><ChevronRight size={16} /></button>
                  </NavButtons>
                </SuggestionHeader>
                <GridContainer>
                  {/* When error occurs, show 6 games from a clean fetch (no search filter) */}
                  <SuggestedGames onClose={onClose} />
                </GridContainer>
              </SuggestionRowContainer>
            </ErrorContainer>
          )}
        </ResultsContainer>
      </HeaderPanel>
    </OverlayContainer>
  );
}

function SuggestedGames({ onClose }) {
  const history = useHistory();
  const { data } = useGameEngine({ limit: 6 });
  const suggested = data || [];
  return (
    <>
      {suggested.map((g, i) => (
        <GridItem key={i} onClick={() => { onClose(); history.push(`/play/${g.id}`); }}>
          <img src={getGameImage(g)} alt={g.name} className="game-img" />
          <i className="fa fa-play play-icon"></i>
        </GridItem>
      ))}
    </>
  );
}
